// Embla carousel bootstrap. Initializes every `[data-embla]` block on the page:
//   <div data-embla data-embla-options='{"align":"center"}'>
//     <button data-embla-prev>…</button>
//     <button data-embla-next>…</button>
//     <div class="embla__viewport"><div class="embla__container">…slides…</div></div>
//   </div>
// Options are read as JSON from data-embla-options. Prev/next buttons (if present)
// are wired and get their [disabled] attribute toggled at the scroll bounds.
//
// Drives every slider in the theme: the homepage solutions carousel, the shared
// components/shared/slider.twig (about capabilities, facility gallery, nav dropdown),
// and the homepage newsroom tabs.
import EmblaCarousel, {
  type EmblaOptionsType,
  type EmblaCarouselType,
  type EmblaEventType,
} from "embla-carousel";

// Horizontal parallax: each slide holds an oversized image inside a
// .embla__parallax__layer; the layer is translated as the carousel scrolls so
// the image drifts within its clip mask. Adapted from Embla's TweenParallax
// example (without the border-radius).
//
// The example scales the factor by snap count, which self-normalizes for a
// loop / single-slide-in-view carousel. Our sliders are non-loop, align:start,
// multiple-in-view, where that produces a drift far larger than the image
// headroom. So the factor is FIXED instead: |diffToTarget| <= 1, hence max
// |translate| = TWEEN_FACTOR * 100% = 15%, kept under static-card's 18% headroom.
const TWEEN_FACTOR = 0.15;

function setupParallax(embla: EmblaCarouselType): void {
  let layers: (HTMLElement | null)[] = [];

  const setLayers = (): void => {
    layers = embla
      .slideNodes()
      .map((slide) => slide.querySelector<HTMLElement>(".embla__parallax__layer"));
  };
  const tween = (_embla: EmblaCarouselType, eventName?: EmblaEventType): void => {
    const engine = embla.internalEngine();
    const scrollProgress = embla.scrollProgress();
    const slidesInView = embla.slidesInView();
    const isScroll = eventName === "scroll";

    embla.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScroll && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
            }
          });
        }

        const translate = diffToTarget * -TWEEN_FACTOR * 100;
        const layer = layers[slideIndex];
        if (layer) layer.style.transform = `translateX(${translate}%)`;
      });
    });
  };

  setLayers();
  tween(embla);
  embla
    .on("reInit", setLayers)
    .on("reInit", tween)
    .on("scroll", tween)
    .on("slideFocus", tween);
}

function setupEmbla(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>(".embla__viewport");
  if (!viewport) return;

  let options: EmblaOptionsType = {};
  if (root.dataset.emblaOptions) {
    try {
      options = JSON.parse(root.dataset.emblaOptions);
    } catch {
      // ignore malformed options — fall back to Embla defaults
    }
  }

  const embla = EmblaCarousel(viewport, options);

  // Nothing to scroll (e.g. a 2-item nav dropdown that fully fits): tear the
  // carousel down so the track can't be dragged. Re-create on resize, since a
  // breakpoint change can alter how many slides fit in view.
  if (!embla.canScrollNext() && !embla.canScrollPrev()) {
    embla.destroy();
    // Disable nav buttons up front — they rely on [disabled] to hide
    // (`[&[disabled]]:opacity-0`), and updateButtons never runs in this branch.
    const prev = root.querySelector<HTMLButtonElement>("[data-embla-prev]");
    const next = root.querySelector<HTMLButtonElement>("[data-embla-next]");
    if (prev) prev.disabled = true;
    if (next) next.disabled = true;
    const initialWidth = viewport.offsetWidth;
    const ro = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === initialWidth) return;
      ro.disconnect();
      initialized.delete(root);
      setupEmbla(root);
    });
    ro.observe(viewport);
    return;
  }

  // Expose the API on the root + emit a bubbling DOM event on every snap change.
  // Lets a thin outer layer (e.g. newsroom's shared arrows / tab routing) drive a
  // carousel and react to its bounds without re-implementing scroll mechanics.
  (root as unknown as { __embla?: EmblaCarouselType }).__embla = embla;
  const emitSelect = (): void => {
    root.dispatchEvent(new CustomEvent("embla:select", { bubbles: true }));
  };
  embla.on("select", emitSelect).on("reInit", emitSelect);
  emitSelect();

  // Grab / grabbing cursor while dragging (Embla sets none by default). The
  // is-dragging class (see components/shared/slider.scss) forces `grabbing`
  // over the whole track — clickable cards' <a> would otherwise keep showing
  // the pointer cursor mid-drag.
  viewport.style.cursor = "grab";
  embla.on("pointerDown", () => {
    viewport.classList.add("is-dragging");
  });
  embla.on("pointerUp", () => {
    viewport.classList.remove("is-dragging");
  });

  const prev = root.querySelector<HTMLButtonElement>("[data-embla-prev]");
  const next = root.querySelector<HTMLButtonElement>("[data-embla-next]");

  // inflight tracks our best-guess destination snap so rapid clicks can't
  // overshoot before the "select" event fires (canScrollNext/Prev reflects
  // the settled position, not the animation target).
  let inflight = embla.selectedScrollSnap();

  const updateButtons = (api: EmblaCarouselType): void => {
    inflight = api.selectedScrollSnap();
    if (prev) prev.disabled = !api.canScrollPrev();
    if (next) next.disabled = !api.canScrollNext();
  };
  updateButtons(embla);
  embla.on("select", updateButtons).on("reInit", updateButtons);

  // Loop carousels have no bounds — skip inflight guard and button state (updateButtons handles it).
  const isLoop = !!options.loop;

  prev?.addEventListener("click", () => {
    if (isLoop) { embla.scrollPrev(); return; }
    if (inflight <= 0) return;
    inflight--;
    embla.scrollPrev();
    if (prev) prev.disabled = inflight <= 0;
    if (next) next.disabled = false;
  });
  next?.addEventListener("click", () => {
    if (isLoop) { embla.scrollNext(); return; }
    const last = embla.scrollSnapList().length - 1;
    if (inflight >= last) return;
    inflight++;
    embla.scrollNext();
    if (next) next.disabled = inflight >= last;
    if (prev) prev.disabled = false;
  });

  // Opt-in: dim non-active slides. data-embla-dim holds the inactive opacity
  // (e.g. "0.2"). Pair with a `transition-opacity` class on the slides for a fade.
  if (root.dataset.emblaDim !== undefined) {
    const inactive = parseFloat(root.dataset.emblaDim) || 0.2;
    const slides = embla.slideNodes();
    const applyDim = (api: EmblaCarouselType): void => {
      const selected = api.selectedScrollSnap();
      slides.forEach((slide, i) => {
        slide.style.opacity = i === selected ? "1" : String(inactive);
      });
    };
    applyDim(embla);
    embla.on("select", applyDim).on("reInit", applyDim);
  }

  // Opt-in horizontal parallax: slides must contain a .embla__parallax__layer.
  if (root.dataset.emblaParallax !== undefined) {
    setupParallax(embla);
  }
}

const initialized = new WeakSet<HTMLElement>();

function initAll(): void {
  document.querySelectorAll<HTMLElement>("[data-embla]").forEach((root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    setupEmbla(root);
  });
}

// Destroy Embla instances before htmx morphs them out. Embla holds an internal
// engine + slide refs + emitter chain that GC can't reach until destroy() runs;
// without this every boosted nav leaks ~10 MB of detached DOM + closures.
function destroyAll(): void {
  document.querySelectorAll<HTMLElement>("[data-embla]").forEach((root) => {
    const api = (root as unknown as { __embla?: EmblaCarouselType }).__embla;
    if (api?.destroy) api.destroy();
    delete (root as unknown as { __embla?: EmblaCarouselType }).__embla;
    initialized.delete(root);
  });
}

document.addEventListener("DOMContentLoaded", initAll);
document.addEventListener("htmx:before-swap", destroyAll);
document.addEventListener("htmx:load", initAll);
